import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TableDetail } from '@iziping/common';
import { apiClient } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import type { ApiError } from '@iziping/common';

export interface TablesResizeRequest {
    count: number;
}

export const useGetTournamentTables = (tournamentId: number) =>
    useQuery<TableDetail[], ApiError>({
        queryKey: [QUERY_CACHE.TOURNAMENT_TABLES, tournamentId],
        queryFn: async () => {
            const res = await apiClient.get<TableDetail[]>(ENDPOINTS.tournamentTables(tournamentId));
            return res.data;
        },
    });

export const useToggleTable = (tournamentId: number) => {
    const qc = useQueryClient();
    return useMutation<TableDetail, ApiError, number>({
        mutationFn: async (tableId) => {
            const res = await apiClient.post<TableDetail>(ENDPOINTS.tournamentTableToggle(tournamentId, tableId));
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_CACHE.TOURNAMENT_TABLES, tournamentId] }),
    });
};

export const useDeleteTable = (tournamentId: number) => {
    const qc = useQueryClient();
    return useMutation<void, ApiError, number>({
        mutationFn: async (tableId) => {
            await apiClient.delete(ENDPOINTS.tournamentTable(tournamentId, tableId));
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_CACHE.TOURNAMENT_TABLES, tournamentId] }),
    });
};

export const useResizeTables = (tournamentId: number) => {
    const qc = useQueryClient();
    return useMutation<TableDetail[], ApiError, TablesResizeRequest>({
        mutationFn: async (body) => {
            const res = await apiClient.put<TableDetail[]>(ENDPOINTS.tournamentTablesResize(tournamentId), body);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_CACHE.TOURNAMENT_TABLES, tournamentId] }),
    });
};
